// Enhanced API Configuration for MUU Platform

// Environment-based API configuration
export const API_CONFIG = {
  baseURL: (import.meta.env as any).VITE_API_BASE_URL,
  timeout: parseInt((import.meta.env as any).VITE_API_TIMEOUT || '10000'),
  retryAttempts: parseInt((import.meta.env as any).VITE_API_RETRY_ATTEMPTS || '3'),
  retryDelay: parseInt((import.meta.env as any).VITE_API_RETRY_DELAY || '1000'),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
};

/**
 * Helper to resolve the Authentication Base URL.
 * Handles path normalization for auth endpoints which might differ from the main API v2 prefix.
 */
export const getAuthBaseUrl = (): string => {
  const baseUrl = API_CONFIG.baseURL || '';
  
  if (!baseUrl) {
    console.warn('VITE_API_BASE_URL is not defined! Authentication requests may fail.');
    return '';
  }

  // Se a baseUrl não começa com http ou https, e não é relativa (/...), assume https
  // Isso evita problemas onde a variável de ambiente vem sem protocolo (ex: www.muu.app/api/v2)
  // e o navegador trata como caminho relativo à URL atual.
  let normalizedUrl = baseUrl;
  if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://') && !baseUrl.startsWith('/')) {
    normalizedUrl = `https://${baseUrl}`;
  }

  // If the base URL points to v2, we strip it because auth endpoints are usually at /api/auth
  // or share the same root /api but not /api/v2
  if (normalizedUrl.includes('/api/v2')) {
    return normalizedUrl.replace('/api/v2', '/api');
  }
  
  if (normalizedUrl.endsWith('/api')) {
    return normalizedUrl;
  }
  
  return `${normalizedUrl.replace(/\/+$/, '')}/api`;
};

// JWT Configuration
export const JWT_CONFIG = {
  accessTokenKey: 'muu_access_token',
  refreshTokenKey: 'muu_refresh_token',
  tokenPrefix: 'Bearer',
  refreshThreshold: 5 * 60 * 1000, // 5 minutes before expiry
  maxRetries: 3,
};

// Test Configuration
export const TEST_CONFIG = {
  // Test token from API example or environment variable
  // Nunca use tokens no frontend; manter sempre null
  testToken: null as string | null,
  // Test endpoints
  testEndpoints: {
    AI_METADATA: '/ai_metadata',
    USER_CREATE: '/auth/users',
    USER_SEARCH: '/Users/search',
  },
};

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REFRESH: '/auth/refresh',
    LOGOUT: '/auth/logout',
    VERIFY: '/auth/verify',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
  },
  // Users
  USERS: {
    BASE: '/Users',
    SEARCH: '/Users/search',
    INSERT: '/Users/insert',
    UPDATE: '/Users/update',
    DELETE: '/Users/delete',
    PROFILE: '/Users/profile',
  },
  // Roles
  ROLES: {
    BASE: '/Roles',
    SEARCH: '/Roles/search',
  },
  // Test endpoints
  TEST: {
    AI_METADATA: '/ai_metadata',
  },
};

// Security Configuration
export const SECURITY_CONFIG = {
  enableTokenEncryption: (import.meta.env as any).VITE_ENABLE_TOKEN_ENCRYPTION === 'true',
  encryptionKey: (import.meta.env as any).VITE_TOKEN_ENCRYPTION_KEY,
  csrfProtection: import.meta.env.VITE_ENABLE_CSRF_PROTECTION === 'true',
  secureCookies: import.meta.env.NODE_ENV === 'production',
};

// Development Configuration
export const DEV_CONFIG = {
  enableDebugLogs: import.meta.env.VITE_ENABLE_DEBUG_LOGS === 'true',
  mockApiDelay: parseInt(import.meta.env.VITE_MOCK_API_DELAY || '0'),
  bypassAuth: import.meta.env.VITE_BYPASS_AUTH === 'true',
};

export default {
  API_CONFIG,
  JWT_CONFIG,
  TEST_CONFIG,
  API_ENDPOINTS,
  SECURITY_CONFIG,
  DEV_CONFIG,
};
